import {
  gql,
  NetworkStatus,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client'
import { useState } from 'react'

import {
  AddItemToCollectionMutation,
  AddItemToCollectionMutationVariables,
  ConsoleCollectionDetailFragmentDoc,
  ConsoleCollectionDetailQuery,
  ConsoleCollectionDetailQueryVariables,
  ConsoleCollectionItemDetailFragmentDoc,
  ConsoleCollectionItemsConnectionFragmentDoc,
  ConsoleItems,
  ConsoleItemsQuery,
  ConsoleItemsQueryVariables,
  ItemType,
  PublishItem,
  PublishItemMutation,
  PublishItemMutationVariables,
  RemoveCollectionMutation,
  RemoveCollectionMutationVariables,
  RemoveItemFromCollectionMutation,
  RemoveItemFromCollectionMutationVariables,
  SalesStatus,
  StartSellingItem,
  StartSellingItemMutation,
  StartSellingItemMutationVariables,
  StopSellingItem,
  StopSellingItemMutation,
  StopSellingItemMutationVariables,
  UnpublishItem,
  UnpublishItemMutation,
  UnpublishItemMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { getAccessToken } from '@/core/auth'
import { createNftItem } from '@/core/web3'
import { connectMetamaskClient, useMetamask } from '@/core/web3/metamask'
import { useWeb3authWalletConnect } from '@/core/web3/web3auth/hooks'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { showMessage, showError } = useMessage()
  const { t } = useTranslation('consoleCollectionDetail')
  const { router, routerPages } = useRouter<{ collectionId: string }>()
  const { collectionId } = router.query
  const [walletConnecting, setWalletConnecting] = useState(false)
  const [nftIssuing, setNftIssuing] = useState(false)
  const { artistAuthorizeWalletBySignatureMessage } = useMetamask()

  const { loading, data, networkStatus, fetchMore, refetch } = useQuery<
    ConsoleCollectionDetailQuery,
    ConsoleCollectionDetailQueryVariables
  >(QUERY, {
    variables: { id: collectionId },
  })

  const walletAddress = data?.artist?.cryptoWallet
  const isNft = !!data?.collection?.nft

  const { web3authWalletLoading } = useWeb3authWalletConnect({
    loading: loading,
    walletAddress: walletAddress?.address,
    isArtist: true,
    refetch,
  })

  const [
    getConsoleItems,
    {
      data: consoleItems,
      loading: gettingConsoleItems,
      fetchMore: fetchMoreConsoleItems,
      refetch: refetchItems,
    },
  ] = useLazyQuery<ConsoleItemsQuery, ConsoleItemsQueryVariables>(
    ConsoleItems,
    { notifyOnNetworkStatusChange: true }
  )

  const [addItemToCollection, { loading: addingItem }] = useMutation<
    AddItemToCollectionMutation,
    AddItemToCollectionMutationVariables
  >(ADD_ITEM_TO_COLLECTION)

  const [removeItemFromCollection, { loading: removingItem }] = useMutation<
    RemoveItemFromCollectionMutation,
    RemoveItemFromCollectionMutationVariables
  >(REMOVE_ITEM_FROM_COLLECTION)

  const [removeCollection, { loading: removingCollection }] = useMutation<
    RemoveCollectionMutation,
    RemoveCollectionMutationVariables
  >(REMOVE_COLLECTION, {
    onCompleted() {
      showMessage(t('deletedTheCollection'), 'success')
      router.goBack(routerPages.ConsoleCollections)
    },
  })

  /**
   * @description
   * コレクションへアイテム追加はコレクション未所属且つ、NFT可能アイテム（audio/video/magazine/wallpaper）のみ
   */
  const acceptedItemTypes = [
    ItemType.Audio,
    ItemType.Video,
    ItemType.Magazine,
    ItemType.Wallpaper,
    ItemType.Avatar,
  ]
  const artistItemsNotInCollection = consoleItems
    ? {
        ...consoleItems?.artistItems,
        edges: consoleItems?.artistItems?.edges?.filter(({ node }) => {
          // コレクション所属済みアイテムは非表示
          if (node.collection) return

          if (
            data?.collection?.collectionItems?.edges?.find(
              collectionItem => collectionItem.node.item.id === node.id
            )
          ) {
            return
          }

          // NFTコレクションの場合、許容するアイテム種別のみ表示
          if (isNft) return acceptedItemTypes.includes(node.type)

          return true
        }),
      }
    : null

  // アイテム編集処理
  const [publishItem] = useMutation<
    PublishItemMutation,
    PublishItemMutationVariables
  >(PublishItem)
  const [unpublishItem] = useMutation<
    UnpublishItemMutation,
    UnpublishItemMutationVariables
  >(UnpublishItem)
  const [startSellingItem] = useMutation<
    StartSellingItemMutation,
    StartSellingItemMutationVariables
  >(StartSellingItem)
  const [stopSellingItem] = useMutation<
    StopSellingItemMutation,
    StopSellingItemMutationVariables
  >(StopSellingItem)

  return {
    state: {
      artistId: user?.uid ?? '',
      collectionId,
      loading: networkStatus === NetworkStatus.loading || web3authWalletLoading,
      fetching: networkStatus === NetworkStatus.fetchMore,
      hasNextPage: !!data?.collection?.collectionItems?.pageInfo?.hasNextPage,
      collection: data?.collection ?? null,
      removingItem,
      removingCollection,
      itemSelectProps: {
        loading: gettingConsoleItems,
        addingItem,
        hasNextPage: !!consoleItems?.artistItems?.pageInfo.hasNextPage,
        items: artistItemsNotInCollection,
      },
      isNft,
      walletConnecting,
      nftIssuing,
      nft: data?.collection?.nft,
      artistWalletAddress: data?.artist?.cryptoWallet?.address,
      event: data?.collection?.event,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            id: collectionId,
            pageInput: {
              cursor: data?.collection?.collectionItems?.pageInfo.endCursor,
            },
          },
        })
      },
      async onRemove() {
        await removeCollection({ variables: { id: collectionId } })
      },
      async onSearchItems() {
        await getConsoleItems()
      },
      async onSelectItems(itemId) {
        setNftIssuing(true)
        return addItemToCollection({
          variables: { collectionId, itemId },
        })
          .then(() => {
            refetch()
            refetchItems && refetchItems()

            if (!isNft) return

            return createNftItem({
              itemId: itemId ?? '',
              artistId: user?.uid ?? '',
              tokenURI: `${config.nft.tokenURIPrefix}UtoniqDigitalItem/items/${itemId}/metadata.json`,
            })
              .then(() => {
                return
              })
              .catch(e => {
                return removeItemFromCollection({
                  variables: {
                    collectionId,
                    itemId,
                  },
                }).then(() => {
                  refetch()

                  throw e
                })
              })
          })
          .finally(() => setNftIssuing(false))
      },
      async onFetchMoreItems() {
        if (!fetchMoreConsoleItems) {
          await getConsoleItems()
          return
        }
        await fetchMoreConsoleItems({
          variables: {
            pageInput: {
              cursor: consoleItems?.artistItems?.pageInfo.endCursor,
            },
          },
        })
      },
      async onConnectMetamask() {
        setWalletConnecting(true)

        if (walletAddress) {
          return connectMetamaskClient()
            .catch(e => showError(e))
            .finally(() => {
              setWalletConnecting(false)
            })
        }

        return artistAuthorizeWalletBySignatureMessage()
          .then(() => refetch())
          .catch(e => showError(e))
          .finally(() => {
            setWalletConnecting(false)
          })
      },
      async onConnectWeb3auth() {
        setWalletConnecting(true)

        if (walletAddress) {
          const idToken = await getAccessToken({ forceUpdateToken: true })
          if (!idToken) throw new Error('get jwtToke failed')

          return import('@/core/web3/web3auth')
            .then(m => m.connectToWeb3Auth(idToken))
            .catch(e => showError(e))
            .finally(() => {
              setWalletConnecting(false)
            })
        }
      },
      async onToggleSelling(id, isSelling) {
        if (isSelling) {
          await stopSellingItem({
            variables: { id },
            optimisticResponse() {
              return {
                stopSellingItem: {
                  id,
                  salesStatus: SalesStatus.Stopped,
                  __typename: 'Item',
                },
              }
            },
          }).catch(showError)
          return
        }
        await startSellingItem({
          variables: { id },
          optimisticResponse() {
            return {
              startSellingItem: {
                id,
                salesStatus: SalesStatus.Active,
                __typename: 'Item',
              },
            }
          },
        }).catch(showError)
      },
      async onToggleActive(id, isActive) {
        if (isActive) {
          await unpublishItem({
            variables: { id },
            optimisticResponse() {
              return {
                unpublishItem: {
                  id,
                  isPublished: false,
                  __typename: 'Item',
                },
              }
            },
          }).catch(showError)
          return
        }
        await publishItem({
          variables: { id },
          optimisticResponse() {
            return {
              publishItem: {
                id,
                isPublished: true,
                __typename: 'Item',
              },
            }
          },
        }).catch(showError)
      },
      async onRemoveItemFromCollection(itemId) {
        await removeItemFromCollection({ variables: { collectionId, itemId } })
          .then(() => {
            showMessage(t('deletedItemFromCollectionSuccess'), 'success')
          })
          .catch(error => showError(error))
      },
    },
  }
})

const QUERY = gql`
  query ConsoleCollectionDetail($id: ID!, $pageInput: PageInput) {
    artist {
      id
      cryptoWallet {
        address
      }
    }
    collection(id: $id) {
      ...ConsoleCollectionDetail
      collectionItems(pageInput: $pageInput) {
        ...ConsoleCollectionItemsConnection
      }
      userCollection {
        id
        ownedItems {
          itemId
          ownedCount
        }
      }
    }
  }
  ${ConsoleCollectionDetailFragmentDoc}
  ${ConsoleCollectionItemsConnectionFragmentDoc}
`

const ADD_ITEM_TO_COLLECTION = gql`
  mutation AddItemToCollection($collectionId: ID!, $itemId: ID!) {
    addItemToCollection(collectionId: $collectionId, itemId: $itemId) {
      ...ConsoleCollectionItemDetail
    }
  }
  ${ConsoleCollectionItemDetailFragmentDoc}
`

const REMOVE_ITEM_FROM_COLLECTION = gql`
  mutation RemoveItemFromCollection($collectionId: ID!, $itemId: ID!) {
    removeItemFromCollection(collectionId: $collectionId, itemId: $itemId) {
      ...ConsoleCollectionItemDetail
    }
  }
  ${ConsoleCollectionItemDetailFragmentDoc}
`

const REMOVE_COLLECTION = gql`
  mutation RemoveCollection($id: ID!) {
    removeCollection(id: $id) {
      id
    }
  }
`
